import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ResourceBundleService } from '@app/services/resource-bundle/resource-bundle.service';
import { ConfigService } from '@core/config.service';
import { get } from 'lodash-es';
import { getMediaEngineId } from '@app/shared/utils/media-engine';
import { RedirectionService } from '@app/services/redirection/redirection.service';
import { SanitizerService } from '@app/services/sanitizer/sanitizer.service';

@Component({
    selector: 'pixie-hero-dcl',
    templateUrl: './hero.component.html',
    styleUrls: ['./hero.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class HeroComponent implements OnInit {
    @Input() id: string;
    @Input() titleMobile: any;
    @Input() titleDesktop: any;
    @Input() ctasMobile: object[];
    @Input() ctasDesktop: object[];
    @Input() src: any;
    @Input() fullscreen: boolean;
    @Input() ME: any;
    @Input() dataType: string;

    hasFullScreenVideo = false;
    siteId: string;
    isOnFullscreen: boolean = false;

    constructor(
        private resourceBundle: ResourceBundleService,
        private configService: ConfigService,
        private redirectionService: RedirectionService,
        private sanitizerService: SanitizerService,
    ) {
        this.siteId = this.configService.getValue('siteId').toUpperCase();
    }

    ngOnInit() {
        this.ctasMobile = [
            {
                alt: '',
                analyticsLinkId: '',
                title: '',
            },
            {
                alt: '',
                analyticsLinkId: '',
                title: '',
                ctaURL: '',
            },
        ];
        this.ctasDesktop = [
            {
                alt: '',
                analyticsLinkId: '',
                title: '',
            },
            {
                alt: '',
                analyticsLinkId: '',
                analyticsLinkIdFullscreen: '',
                title: '',
                ctaURL: '',
            },
        ];
        this.resourceBundle.get('heroes').then(data => {
            const descriptions = data.heroes[0].descriptions;
            if (get(data, 'heroes[0].descriptions.defaultImage.sections.source', false)
            && descriptions?.defaultImage?.sections?.source?.length > 1) {
                this.dataType = 'image';
                this.titleMobile = this.sanitizerService.decodeHTML(descriptions?.mobileDefaultImage?.sections?.alt);
                this.ctasMobile = [
                    {
                        alt: '',
                        analyticsLinkId: '',
                        title: '',
                    },
                    {
                        alt: this.sanitizerService.decodeHTML(descriptions?.mobileVideoDetailsCTA?.sections?.alt),
                        analyticsLinkId: descriptions?.mobileVideoDetailsCTA?.sections?.analyticsLinkId,
                        title: this.sanitizerService.decodeHTML(descriptions?.mobileVideoDetailsCTA?.sections?.title),
                        ctaURL: descriptions?.mobileVideoDetailsCTA?.sections?.ctaURL,
                    }
                ];
                this.titleDesktop = this.sanitizerService.decodeHTML(descriptions?.defaultImage?.sections?.alt);
                this.ctasDesktop = [
                    {
                        alt: '',
                        analyticsLinkId: '',
                        title: '',
                    },
                    {
                        alt: this.sanitizerService.decodeHTML(descriptions?.videoDetailsCTA?.sections?.alt),
                        analyticsLinkId: this.siteId + '_HPMP_Billboard_' +
                            data.heroes[0].descriptions.videoDetailsCTA.sections.analyticsLinkId +
                            '_Link',
                        analyticsLinkIdFullscreen: this.siteId + '_HPMP_InVideo_' +
                            data.heroes[0].descriptions.videoDetailsCTA.sections.analyticsLinkId +
                            '_Link',
                        title: this.sanitizerService.decodeHTML(descriptions?.videoDetailsCTA?.sections?.title),
                        ctaURL: data.heroes[0].descriptions.videoDetailsCTA.sections.ctaURL,
                    }
                ];

                // This gets used from within the frame
                window['heroMedia'] = [{
                    type: 'image',
                    thumbnail: this.getResizeImageUrl(
                        descriptions.defaultImage.sections.source
                    ),
                    source: [
                        descriptions.defaultImage.sections.source
                    ],
                    preview: [
                        descriptions.defaultImage.sections.source
                    ],
                    poster: this.getResizeImageUrl(
                        descriptions.defaultImage.sections.source
                    ),
                    title: this.siteId,
                    alt: this.siteId,
                    tags: ['a']
                }];
            } else if (get(data, 'heroes[0].descriptions', false)) {
                this.titleMobile = this.sanitizerService.decodeHTML(descriptions?.mobileVideoTitle?.sections?.title);
                this.ctasMobile = [
                    {
                        alt: this.sanitizerService.decodeHTML(descriptions?.mobileVideoPlayCTA?.sections?.alt),
                        analyticsLinkId: descriptions.mobileVideoPlayCTA.sections.analyticsLinkId,
                        title: this.sanitizerService.decodeHTML(descriptions?.mobileVideoPlayCTA?.sections?.title),
                    },
                    {
                        alt: this.sanitizerService.decodeHTML(descriptions?.mobileVideoDetailsCTA?.sections?.alt),
                        analyticsLinkId: data.heroes[0].descriptions.mobileVideoDetailsCTA.sections.analyticsLinkId,
                        title: this.sanitizerService.decodeHTML(descriptions?.mobileVideoDetailsCTA?.sections?.title),
                        ctaURL: descriptions?.mobileVideoDetailsCTA?.sections?.ctaURL,
                    }
                ];
                this.titleDesktop = this.sanitizerService.decodeHTML(descriptions?.videoTitle?.sections?.title);
                this.ctasDesktop = [
                    {
                        alt: this.sanitizerService.decodeHTML(descriptions?.videoPlayCTA?.sections?.alt),
                        analyticsLinkId: descriptions.videoPlayCTA.sections.analyticsLinkId,
                        title: this.sanitizerService.decodeHTML(descriptions?.videoPlayCTA?.sections?.title),
                    },
                    {
                        alt: this.sanitizerService.decodeHTML(descriptions?.videoDetailsCTA?.sections?.alt),
                        analyticsLinkId: this.siteId + '_HPMP_Billboard_' +
                            descriptions?.videoDetailsCTA?.sections?.analyticsLinkId + '_Link',
                        analyticsLinkIdFullscreen: this.siteId + '_HPMP_InVideo_' +
                            descriptions.videoDetailsCTA.sections.analyticsLinkId +
                            '_Link',
                        title: this.sanitizerService.decodeHTML(descriptions?.videoDetailsCTA?.sections?.title),
                        ctaURL: descriptions.videoDetailsCTA.sections.ctaURL,
                    }
                ];

                this.hasFullScreenVideo = true; // set to include video tap
                // This gets used from within the frame
                if (get(data, 'heroes[0].descriptions.videoDetail.sections.source', false)) {
                    window['heroMedia'] = [{
                        type: 'video',
                        thumbnail: this.getResizeImageUrl(
                            descriptions?.defaultImage?.sections?.source
                        ),
                        source: [
                            descriptions?.videoMain?.sections?.source1,
                            descriptions?.videoMain?.sections?.source2
                        ],
                        preview: [
                            descriptions?.videoMain?.sections?.source1,
                            descriptions?.videoMain?.sections?.source2
                        ],
                        poster: this.getResizeImageUrl(
                            descriptions?.defaultImage?.sections?.source
                        ),
                        placeholders: [
                            descriptions?.videoMain?.sections?.placeholder,
                            descriptions?.videoMain?.sections?.placeholderSmall,
                            descriptions?.videoMain?.sections?.placeholderLarge
                        ],
                        title: this.siteId,
                        alt: this.siteId,
                        tags: ['a']
                    },
                    {
                        type: 'video',
                        thumbnail: this.getResizeImageUrl(
                            descriptions?.defaultImage?.sections?.source
                        ),
                        source: [
                            descriptions?.videoDetail?.sections?.source1,
                            descriptions?.videoDetail?.sections?.source2
                        ],
                        preview: [
                            descriptions?.videoDetail?.sections?.source1,
                            descriptions?.videoDetail?.sections?.source2
                        ],
                        poster: this.getResizeImageUrl(
                            descriptions?.defaultImage?.sections?.source
                        ),
                        placeholders: [
                            descriptions?.videoMain?.sections?.placeholder,
                            descriptions?.videoMain?.sections?.placeholderSmall,
                            descriptions?.videoMain?.sections?.placeholderLarge
                        ],
                        closedCaptioning: descriptions?.videoDetail?.sections?.closedCaption,
                        descriptiveTranscript: descriptions?.videoDetail?.sections?.descriptiveTranscript,
                        title: this.siteId,
                        alt: this.siteId,
                        tags: ['b']
                    }];
                } else {
                    window['heroMedia'] = [{
                        type: 'video',
                        thumbnail: this.getResizeImageUrl(
                            descriptions?.defaultImage?.sections?.source
                        ),
                        source: [
                            descriptions?.videoMain?.sections?.source1,
                            descriptions?.videoMain?.sections?.source2
                        ],
                        preview: [
                            descriptions?.videoMain?.sections?.source1,
                            descriptions?.videoMain?.sections?.source2
                        ],
                        poster: this.getResizeImageUrl(
                            descriptions?.defaultImage?.sections?.source
                        ),
                        placeholders: [
                            descriptions?.videoMain?.sections?.placeholder,
                            descriptions?.videoMain?.sections?.placeholderSmall,
                            descriptions?.videoMain?.sections?.placeholderLarge
                        ],
                        title: this.siteId,
                        alt: this.siteId,
                        tags: ['a']
                    }];
                }
            }
        });

    }

    getResizeImageUrl(url) {
        const isMobile = 'ontouchstart' in window && window.screen.width < 768;
        const isValidImage = url.indexOf('.jpg') !== -1;
        const alreadyResized = url.indexOf('/resize/') !== -1;
        const version = '2';
        const compression = '75';
        let dimensions = '1280/720';
        let cdnUrl = url;

        if (isValidImage && !alreadyResized) {
            if (isMobile) {
                dimensions = window.innerWidth > 0 && window.innerWidth < 433 ?
                    window.innerWidth + '' :
                    '433';
                dimensions += '/433';
            }

            cdnUrl = cdnUrl.replace(
                '/dam/',
                `/resize/mwImage/${version}/${dimensions}/${compression}/dam/`
            );
        }

        return cdnUrl;
    }

    enterFullscreen(event) {
        event.preventDefault();
        const appComponentMediaEngineId = getMediaEngineId('appComponent', 1);
        const appComponentMediaEngineInstance = window[appComponentMediaEngineId];
        if (!appComponentMediaEngineInstance) {
            window['initConfigOverrides']();
        }

        if (this.hasFullScreenVideo && appComponentMediaEngineInstance.config.callbacks.customFullscreenEnter) {
            appComponentMediaEngineInstance.config.callbacks.customFullscreenEnter();
            this.isOnFullscreen = true;
        }
    }

    redirectToUrl(event) {
        this.isOnFullscreen = this.redirectionService.redirectToUrl(event, this.isOnFullscreen, () => this.getCtaUrl());
    }

    getCtaUrl(): string {
        return this.redirectionService.getCtaUrlTransformed(this.ctasMobile, this.ctasDesktop);
    }

}
